import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {BrowserMultiFormatReader, IScannerControls} from '@zxing/browser';
import {NotFoundException} from '@zxing/library';

@Component({
  selector: 'app-qr-scanner-pane',
  templateUrl: './qr-scanner-pane.component.html',
  standalone: false,
})
export class QrScannerPaneComponent implements OnDestroy {
  /** i18n key for the scanner title. Defaults to PIX QR copy. */
  @Input() titleKey = 'features.withdrawals.modalPix.qrScannerTitle';

  /** i18n key when camera permission is denied. */
  @Input() permissionDeniedKey =
    'features.withdrawals.modalPix.qrScannerPermissionDenied';

  @Input() set active(value: boolean) {
    this.isActive = value;
    if (value) {
      void this.startScanning();
    } else {
      this.stopScanning();
    }
  }

  @Output() readonly qrDetected = new EventEmitter<string>();

  @Output() readonly backRequested = new EventEmitter<void>();

  @ViewChild('preview', {static: true})
  private previewRef?: ElementRef<HTMLVideoElement>;

  permissionDenied = false;
  scanning = false;

  private isActive = false;
  private reader?: BrowserMultiFormatReader;
  private scannerControls?: IScannerControls;
  private detected = false;

  ngOnDestroy(): void {
    this.stopScanning();
  }

  private async startScanning(): Promise<void> {
    if (!this.isActive || this.detected) {
      return;
    }

    this.permissionDenied = false;
    this.scanning = true;
    this.reader ??= new BrowserMultiFormatReader();

    const video = this.previewRef?.nativeElement;
    if (!video) {
      this.scanning = false;
      return;
    }

    try {
      this.scannerControls = await this.reader.decodeFromVideoDevice(
        undefined,
        video,
        (result, error) => {
          if (result && !this.detected) {
            this.detected = true;
            this.stopScanning();
            this.qrDetected.emit(result.getText());
            return;
          }

          if (
            error &&
            !(error instanceof NotFoundException) &&
            error.name === 'NotAllowedError'
          ) {
            this.permissionDenied = true;
            this.stopScanning();
          }
        },
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        this.permissionDenied = true;
      }
      this.stopScanning();
    }
  }

  private stopScanning(): void {
    this.scanning = false;
    this.scannerControls?.stop();
    this.scannerControls = undefined;
  }

  reset(): void {
    this.detected = false;
    this.permissionDenied = false;
    if (this.isActive) {
      void this.startScanning();
    }
  }
}
